/** Platform analytics (`/analytics`). */

import type { PriceEstimate } from '../../schemas/platform/pricing';
import { EndpointPricing, EndpointStats, SimilarService } from '../../schemas/platform/pricing';
import { BackendClient } from './BackendClient';

export class AnalyticsClient extends BackendClient {
  constructor(apiKey?: string, webBackendUrl?: string) {
    super(apiKey, webBackendUrl);
    this.addEndpoint('analytics/estimate', 'POST', 'none');
    this.addEndpoint('analytics/stats', 'POST', 'none');
    this.addEndpoint('analytics/similar-services', 'GET', 'none');
    this.addEndpoint('analytics/service-pricing', 'GET', 'optional');
  }

  async estimate(
    deploymentId: string,
    inputData: Record<string, any> = {},
    endpointId?: string | null,
  ): Promise<PriceEstimate> {
    return this.postJson('analytics/estimate', inputData, {
      deployment_id: deploymentId,
      endpoint_id: endpointId ?? null,
    });
  }

  async getStats(deploymentId?: string | null, endpointPath?: string | null): Promise<EndpointStats[]> {
    const raw = await this.postJson('analytics/stats', undefined, {
      deployment_id: deploymentId ?? undefined,
      endpoint_path: endpointPath ?? undefined,
    });
    const rows = Array.isArray(raw) ? raw : [];
    return rows.map((row: any) => new EndpointStats(row));
  }

  async getSimilarServices(serviceId: string, k = 8): Promise<SimilarService[]> {
    const raw = await this.getJson('analytics/similar-services', { service_id: serviceId, k });
    const rows = Array.isArray(raw) ? raw : [];
    return rows.map((row: any) => new SimilarService(row));
  }

  async getServicePricing(
    serviceId?: string | null,
    deploymentId?: string | null,
    endpointId?: string | null,
  ): Promise<EndpointPricing[]> {
    const raw = await this.getJson('analytics/service-pricing', {
      service_id: serviceId ?? undefined,
      deployment_id: deploymentId ?? undefined,
      endpoint_id: endpointId ?? undefined,
    });
    const rows = Array.isArray(raw) ? raw : [];
    return rows.map((row: any) => new EndpointPricing(row));
  }
}
